import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Users, MessageSquarePlus, Target, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { MEDIA, CATEGORY_IMG, GUIDE_IMG } from "@/lib/media";
import { Icon } from "@/lib/icons";
import ThreadCard from "@/components/ThreadCard";
import NewsletterForm from "@/components/NewsletterForm";

const GOALS = [
  { key: "build_strength", label: "Build Strength", icon: "Dumbbell" },
  { key: "lose_weight", label: "Lose Weight", icon: "Scale" },
  { key: "improve_mobility", label: "Improve Mobility", icon: "Move" },
  { key: "feel_better", label: "Feel Better", icon: "Heart" },
  { key: "start_again", label: "Start Again", icon: "RotateCcw" },
];

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

export default function Home() {
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [guides, setGuides] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/threads/trending", { params: { limit: 4 } }).then((r) => setTrending(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/guides").then((r) => setGuides(r.data.slice(0, 3))).catch(() => {});
    api.get("/challenges/14-day-stronger-you").then((r) => setChallenge(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-14 pb-10 lg:pt-20 lg:pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-plum">
              <span className="block text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-teal mb-4">StrongKind Circle</span>
              Get stronger. Move better.<br />Age well—<span className="font-script text-teal text-5xl sm:text-6xl lg:text-7xl">together.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-plum-ink/80 max-w-xl leading-relaxed">
              A warm, honest circle for women 45+ who want to feel strong in their bodies. Real conversations,
              gentle guidance, and challenges you can actually finish.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/join" data-testid="hero-join-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-teal px-8 py-4 font-semibold text-white text-lg hover:bg-teal-dark hover:-translate-y-1 transition-[transform,background-color] duration-300">
                Join the Circle <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/newsletter" data-testid="hero-newsletter-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-rose/40 px-8 py-4 font-semibold text-plum text-lg hover:bg-rose/60 transition-colors">
                Get the Newsletter
              </Link>
            </div>
            <p className="mt-5 text-sm text-mutedplum">New discussions daily · No fake numbers, ever · Free to join</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="relative">
            <div className="absolute -top-6 -left-6 h-40 w-40 rounded-full bg-rose/40 -z-10" />
            <div className="absolute -bottom-8 -right-6 h-28 w-28 rounded-full bg-teal/15 -z-10" />
            <img src={MEDIA.hero} alt="Four women over 45 laughing together after a strength and mobility session at StrongKind Circle"
              className="rounded-[2rem] shadow-soft w-full h-[420px] lg:h-[520px] object-cover border-4 border-white" />
          </motion.div>
        </div>
      </section>

      {/* GOAL SELECTOR */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <motion.div {...fade} className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl text-plum">What's your goal right now?</h2>
          <p className="mt-3 text-mutedplum text-lg">Pick one and we'll take you straight to the conversations that fit.</p>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {GOALS.map((g, i) => (
            <motion.button key={g.key} {...fade} transition={{ duration: 0.4, delay: i * 0.06 }}
              onClick={() => navigate(`/community?goal=${g.key}`)} data-testid={`goal-${g.key}`}
              className="group bg-white rounded-3xl border border-rose/30 shadow-card p-6 flex flex-col items-center gap-4 hover:-translate-y-1 hover:shadow-soft transition-[transform,box-shadow] duration-300">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-rose/30 text-teal group-hover:bg-teal group-hover:text-white transition-colors">
                <Icon name={g.icon} className="h-7 w-7" />
              </span>
              <span className="font-semibold text-plum text-center leading-tight">{g.label}</span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* TRENDING + CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-3xl text-plum">Trending discussions</h2>
            <Link to="/community" className="text-teal font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="see-all-discussions">
              See all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {trending.map((t, i) => <ThreadCard key={t.thread_id} thread={t} index={i} />)}
          </div>
        </div>
        <div>
          <h2 className="font-display text-3xl text-plum mb-6">Community categories</h2>
          <div className="bg-white rounded-3xl border border-rose/30 shadow-card p-3">
            {categories.map((c) => (
              <Link key={c.slug} to={`/community/${c.slug}`} data-testid={`category-link-${c.slug}`}
                className="flex items-center gap-3 p-3 rounded-2xl hover:bg-rose-soft transition-colors group">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-rose/30 text-teal shrink-0">
                  <Icon name={c.icon} className="h-5 w-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-plum text-[15px] leading-tight group-hover:text-teal transition-colors">{c.name}</span>
                  <span className="block text-xs text-mutedplum">{c.thread_count} {c.thread_count === 1 ? "discussion" : "discussions"}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-mutedplum opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CHALLENGE */}
      {challenge && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <motion.div {...fade} className="relative overflow-hidden rounded-[2.5rem] bg-plum text-cream grain">
            <div className="grid lg:grid-cols-2 items-center">
              <div className="p-8 sm:p-12 lg:p-16">
                <span className="inline-flex items-center gap-2 rounded-full bg-rose/20 px-4 py-1.5 text-sm font-semibold text-rose">
                  <Target className="h-4 w-4" /> Featured Challenge
                </span>
                <h2 className="mt-5 font-display text-3xl sm:text-4xl text-cream">{challenge.title}</h2>
                <p className="mt-4 text-cream/80 text-lg leading-relaxed max-w-lg">{challenge.description}</p>
                <Link to={`/challenges/${challenge.slug}`} data-testid="featured-challenge-btn"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 font-semibold text-white hover:bg-teal-dark hover:-translate-y-1 transition-[transform,background-color] duration-300">
                  Start the challenge <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
              <div className="h-64 lg:h-full min-h-[320px]">
                <img src={MEDIA.hero} alt={`Women 45+ training together in the ${challenge.title}`} className="h-full w-full object-cover" />
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* NEWSLETTER */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-14">
        <motion.div {...fade} className="rounded-[2rem] bg-white border border-rose/30 shadow-soft p-8 sm:p-12 text-center">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose/30 text-teal mx-auto">
            <Users className="h-7 w-7" />
          </span>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl text-plum">Join StrongKind Weekly</h2>
          <p className="mt-3 text-mutedplum text-lg max-w-xl mx-auto">
            One honest email a week—practical tips on strength, protein and mobility, plus the best of the Circle.
          </p>
          <div className="mt-7 max-w-xl mx-auto"><NewsletterForm source="homepage" /></div>
        </motion.div>
      </section>

      {/* FEATURED GUIDES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <motion.div {...fade} className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl text-plum">Featured guides & resources</h2>
            <p className="mt-2 text-mutedplum text-lg">Cornerstone reads to help you start with confidence.</p>
          </div>
          <Link to="/guides" className="hidden sm:inline-flex text-teal font-semibold items-center gap-1 hover:gap-2 transition-all" data-testid="see-all-guides">
            All guides <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {guides.map((g, i) => (
            <motion.div key={g.slug} {...fade} transition={{ duration: 0.4, delay: i * 0.08 }}>
              <Link to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`}
                className="group block bg-white rounded-3xl border border-rose/30 shadow-card overflow-hidden hover:-translate-y-1 hover:shadow-soft transition-[transform,box-shadow] duration-300 h-full">
                <div className="h-44 overflow-hidden">
                  <img src={GUIDE_IMG[g.image_key] || MEDIA.hero} alt={g.title}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl text-plum leading-snug group-hover:text-teal transition-colors">{g.title}</h3>
                  <p className="mt-2 text-[15px] text-mutedplum line-clamp-2">{g.subtitle}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS + CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <motion.h2 {...fade} className="font-display text-3xl sm:text-4xl text-plum text-center mb-12">How it works</motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: "HeartHandshake", n: "1", t: "Join the Circle", d: "Create a free account and tell us your goal. It takes a minute." },
            { icon: "MessageCircle", n: "2", t: "Find your people", d: "Jump into honest discussions and ask anything—no judgment, ever." },
            { icon: "Sparkles", n: "3", t: "Get a little stronger", d: "Follow a challenge at your own pace and celebrate real progress." },
          ].map((s, i) => (
            <motion.div key={s.n} {...fade} transition={{ duration: 0.4, delay: i * 0.1 }} className="text-center">
              <span className="relative inline-flex h-20 w-20 items-center justify-center rounded-full bg-rose/30 text-teal mx-auto">
                <Icon name={s.icon} className="h-8 w-8" />
                <span className="absolute -top-1 -right-1 h-7 w-7 rounded-full bg-plum text-cream text-sm font-bold flex items-center justify-center">{s.n}</span>
              </span>
              <h3 className="mt-5 font-display text-2xl text-plum">{s.t}</h3>
              <p className="mt-2 text-mutedplum text-[15px] max-w-xs mx-auto">{s.d}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fade} className="mt-16 rounded-[2rem] bg-teal text-white text-center p-10 sm:p-14 grain">
          <h2 className="font-display text-3xl sm:text-4xl text-white">Ready to feel strong again?</h2>
          <p className="mt-3 text-white/85 text-lg max-w-xl mx-auto">You don't have to do this alone. The Circle is here, and we'd love to meet you.</p>
          <Link to="/join" data-testid="cta-join-btn"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-teal text-lg hover:-translate-y-1 transition-transform duration-300">
            Join the Circle <ArrowRight className="h-5 w-5" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
